import { Card, CardContent, CardActions } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ButtonLink } from '@/components/ButtonLink'
import { BusinessDetailsPendingComponent } from '@/features/admin/businesses/detail/components/BusinessDetailsPendingComponent'
import { BusinessDetailsTableLayout } from '@/features/admin/businesses/detail/components/BusinessDetailsTableLayout'
import { useBusinessLocationsDetailedColumns } from '@/features/admin/businesses/detail/helper/businessLocationsDetailedColumns'
import { assertUserIsAdminOrRedirect } from '@/features/admin/helper/assertUserIsAdminOrRedirect'
import { DeletedItemSelection } from '@/generated/Enumerations/DeletedItemSelection_pb'
import {
    businessDetailsQueryOptions,
    useBusinessDetails,
} from '@/queries/useBusinessDetails'
import { availablePageSizes } from '@/schemas/searchParams/searchParamsPaginationSchema'

const seleniumPrefix = 'view-businesses'

const RouteComponent: React.FC = () => {
    const { id } = Route.useParams()
    const {
        data: { business, businessLocations },
    } = useBusinessDetails(id, DeletedItemSelection.BOTH)

    const { t } = useTranslation()

    const columns = useBusinessLocationsDetailedColumns()

    return (
        <Card>
            <CardContent>
                <h3>{t('business.business')}</h3>
                <BusinessDetailsTableLayout
                    businessName={business?.name}
                    description={business?.description}
                />
                <h3>{t('business.location')}</h3>
                <DataGrid
                    rows={businessLocations}
                    columns={columns}
                    pageSizeOptions={availablePageSizes}
                    paginationMode={'client'}
                    sortingMode={'client'}
                    getRowId={({ name }) => name}
                    sx={{ marginTop: 2 }}
                />
            </CardContent>
            <CardActions style={{ justifyContent: 'flex-end' }}>
                <ButtonLink
                    to={'/admin/businesses'}
                    data-selenium={`${seleniumPrefix}-back`}
                >
                    {t('common.back')}
                </ButtonLink>
                <ButtonLink
                    params={{ id }}
                    to={'/admin/businesses/$id/edit'}
                    variant={'outlined'}
                    data-selenium={`${seleniumPrefix}-edit`}
                >
                    {t('common.edit')}
                </ButtonLink>
            </CardActions>
        </Card>
    )
}

export const Route = createFileRoute(
    '/__base-layout/admin/businesses/$id/view'
)({
    component: RouteComponent,
    beforeLoad: async ({
        context: { user, queryClient, grpcClients },
        params: { id },
    }) => {
        assertUserIsAdminOrRedirect(user)

        await queryClient.ensureQueryData(
            businessDetailsQueryOptions(
                grpcClients,
                id,
                DeletedItemSelection.BOTH
            )
        )
    },
    pendingComponent: BusinessDetailsPendingComponent,
})
